import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Button,
    FormControl,
    Glyphicon,
    Label,
    Table,
} from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
    assignCapToUnit,
    createCapForUnit,
    createUnitNote,
    getCapsAdminOnly,
    getUnit,
    getUnitCaps,
    getUnitHealthLog,
    getUnitNotes,
    getUnits,
    getUnitSim,
    patchCapUpdates,
    patchUnit,
    replaceUnit,
    unactivateCap,
} from "../utils/bedrockAPI";
import { formatPhoneNumber, getUser } from "../utils/Common";
import CapModal from "../components/CapModal";
import UnitSimPanel from "./UnitSimPanel";
import UnitPortField from "./UnitPortField";
import {
    STATUS_OPTIONS,
    apiErrorMessage,
    formatDate,
    formatDateOnly,
    formatTimezoneDisplay,
    formatUpdateAvailableDate,
    findBySticker,
    healthStyle,
    httpsKeysDisplay,
    softwareStatusStyle,
    softwareUpdates,
    statusStyle,
    capShortId,
    capSiteLabel,
    pendingReplaceMessage,
    sticker,
    unitLifecycle,
    yesNo,
} from "../utils/unitDisplay";
import {isHardwareSim} from "../utils/simDisplay";
import "./Units.css";
import "./Admin.css";

const NOTE_TYPES = ["general", "repair", "warehouse"];

function capStatusStyle(status) {
    switch ((status || "").toUpperCase()) {
        case "ACTIVE":
            return "success";
        case "BUILD":
            return "info";
        case "DECOMISSIONED":
            return "warning";
        case "DELETE":
            return "danger";
        default:
            return "default";
    }
}

function formatHealth(health) {
    if (health == null || health === "") return "—";
    if (typeof health === "string") return health;
    try {
        return JSON.stringify(health);
    } catch (e) {
        return String(health);
    }
}

export default function UnitDetail({ match, history }) {
    const unitId = match.params.unitId;
    const [unit, setUnit] = useState(null);
    const [caps, setCaps] = useState([]);
    const [notes, setNotes] = useState([]);
    const [healthLog, setHealthLog] = useState([]);
    const [sim, setSim] = useState(null);
    const [noteText, setNoteText] = useState("");
    const [noteType, setNoteType] = useState("general");
    const [loading, setLoading] = useState(true);
    const [savingStatus, setSavingStatus] = useState(false);
    const [cycling, setCycling] = useState(false);
    const [savingNote, setSavingNote] = useState(false);
    const [replacing, setReplacing] = useState(false);
    const [unactivating, setUnactivating] = useState(false);
    const [scheduling, setScheduling] = useState(false);
    const [assigningCap, setAssigningCap] = useState(false);
    const [capStickerInput, setCapStickerInput] = useState("");
    const [replacementStickerInput, setReplacementStickerInput] = useState("");
    const [showCapModal, setShowCapModal] = useState(false);
    const skipModalReload = useRef(true);
    const [error, setError] = useState(null);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [unitRow, capRows, noteRows, healthRows] = await Promise.all([
                getUnit(unitId),
                getUnitCaps(unitId),
                getUnitNotes(unitId),
                getUnitHealthLog(unitId).catch(() => []),
            ]);
            let simRow = null;
            try {
                simRow = await getUnitSim(unitId);
            } catch (err) {
                if (err?.response?.status !== 404) {
                    throw err;
                }
            }
            setUnit(unitRow);
            setCaps(Array.isArray(capRows) ? capRows : []);
            setNotes(Array.isArray(noteRows) ? noteRows : []);
            setHealthLog(Array.isArray(healthRows) ? healthRows : []);
            setSim(simRow && isHardwareSim(simRow) ? simRow : null);
        } catch (err) {
            setError(apiErrorMessage(err));
            setUnit(null);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (getUser().role !== "ADMIN") {
            history.push("/dashboard");
            return;
        }
        loadData();
    }, [unitId]);

    useEffect(() => {
        if (skipModalReload.current) {
            skipModalReload.current = false;
            return;
        }
        if (!showCapModal) {
            loadData();
        }
    }, [showCapModal]);

    const currentCap = unit?.currentCapId
        ? caps.find((cap) => cap.capId === unit.currentCapId)
        : null;
    const lifecycle = unitLifecycle(unit, currentCap);
    const updates = softwareUpdates(unit, currentCap);

    const handleStatusChange = async (nextStatus) => {
        if (!unit || nextStatus === lifecycle) return;
        if (
            nextStatus === "RETIRED" &&
            !window.confirm("Retire this unit? It will stay in history but should not be shipped again.")
        ) {
            return;
        }
        setSavingStatus(true);
        setError(null);
        try {
            const updated = await patchUnit(unit.unitId, { status: nextStatus });
            setUnit(updated);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSavingStatus(false);
    };

    const handleMarkArrived = async () => {
        if (!unit) return;
        setSavingStatus(true);
        setError(null);
        try {
            const updated = await patchUnit(unit.unitId, {status: "ARRIVED"});
            setUnit(updated);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSavingStatus(false);
    };

    const handleScheduleUpdate = async () => {
        if (!unit?.currentCapId) return;
        setScheduling(true);
        setError(null);
        const merged = {
            ...updates,
            status: "UPDATE_AVAILABLE",
            update_available: formatUpdateAvailableDate(),
        };
        try {
            await patchCapUpdates(unit.currentCapId, merged);
            setUnit((prev) => (prev ? {...prev, updates: merged} : prev));
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setScheduling(false);
    };

    const handleCycle = async () => {
        if (!unit) return;
        const currentLabel = currentCap
            ? `${currentCap.name || sticker(currentCap.capId)} (${currentCap.status || "unknown"})`
            : "none";
        if (
            !window.confirm(
                `Return this unit to inventory? Sticker ${sticker(unit.unitId)} stays the same. A new BUILD cap is created for the next customer. Current rental (${currentLabel}) will be decommissioned.`
            )
        ) {
            return;
        }
        setCycling(true);
        setError(null);
        try {
            await createCapForUnit(unit.unitId);
            await loadData();
        } catch (err) {
            setError(apiErrorMessage(err));
            setCycling(false);
            return;
        }
        setCycling(false);
    };

    const handleAssignExisting = async () => {
        if (!unit) return;
        setError(null);
        try {
            const buildCaps = await getCapsAdminOnly(["BUILD"]);
            const {match, matches} = findBySticker(buildCaps, capStickerInput, "capId");
            if (!match) {
                setError(
                    matches.length > 1
                        ? "That sticker matches more than one BUILD cap."
                        : "No BUILD cap found for that sticker."
                );
                return;
            }
            if (match.capId === unit.currentCapId) {
                setError("That cap is already current on this unit.");
                return;
            }
            if (match.unitId && match.unitId !== unit.unitId) {
                if (
                    !window.confirm(
                        `BUILD cap ${sticker(match.capId)} is on unit ${sticker(match.unitId)}. Move it to ${sticker(unit.unitId)}? The current rental on this unit will be decommissioned.`
                    )
                ) {
                    return;
                }
            } else if (
                !window.confirm(
                    `Assign BUILD cap ${sticker(match.capId)} to sticker ${sticker(unit.unitId)}? The current rental will be decommissioned.`
                )
            ) {
                return;
            }
            setAssigningCap(true);
            await assignCapToUnit(unit.unitId, match.capId);
            setCapStickerInput("");
            await loadData();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setAssigningCap(false);
    };

    const handleReplace = async (replacementUnitId) => {
        if (!unit?.currentCapId) return;
        const keepCap = sticker(unit.currentCapId);
        const confirmText = replacementUnitId
            ? `Stage ${sticker(replacementUnitId)} to replace this unit? Cap ${keepCap} stays on this box until the replacement boots and polls assignment. Power the spare off before staging.`
            : `Create a new unit sticker and stage it to replace this one? Cap ${keepCap} stays here until that new box boots.`;
        if (!window.confirm(confirmText)) {
            return;
        }
        setReplacing(true);
        setError(null);
        try {
            const created = await replaceUnit(unit.unitId, replacementUnitId);
            if (!replacementUnitId && created?.unitId) {
                history.push(`/admin/units/${created.unitId}`);
                return;
            }
            setReplacementStickerInput("");
            await loadData();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setReplacing(false);
    };

    const handleCancelStagedReplace = async () => {
        const spareId = unit?.replacesUnitId ? unit.unitId : unit?.replacedByUnitId;
        if (!spareId) return;
        if (!window.confirm("Cancel the staged replacement? The field unit keeps its current cap.")) {
            return;
        }
        setReplacing(true);
        setError(null);
        try {
            await patchUnit(spareId, {replacesUnitId: null});
            await loadData();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setReplacing(false);
    };

    const handleReplaceWithSticker = async () => {
        if (!unit) return;
        setError(null);
        try {
            const rows = await getUnits();
            const {match, matches} = findBySticker(rows, replacementStickerInput, "unitId");
            if (!match) {
                setError(
                    matches.length > 1
                        ? "That sticker matches more than one unit."
                        : "No unit found for that sticker."
                );
                return;
            }
            if (match.unitId === unit.unitId) {
                setError("Pick a different hardware unit.");
                return;
            }
            await handleReplace(match.unitId);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
    };

    const handleUnactivate = async () => {
        if (!unit?.currentCapId) return;
        const capName = currentCap?.name || capShortId(currentCap, unit.currentCapId);
        if (
            !window.confirm(
                `Unactivate cap ${capName}?\n\n` +
                    `This will:\n` +
                    `• Set cap status back to BUILD\n` +
                    `• Remove all users who have access to this cap\n` +
                    `• Reset the cap name to the unit sticker (${sticker(unit.unitId)})\n` +
                    `• Clear the activation date and neighborhood\n\n` +
                    `The unit stays on this hardware. Someone will need to activate it again.`
            )
        ) {
            return;
        }
        setUnactivating(true);
        setError(null);
        try {
            await unactivateCap(unit.currentCapId);
            await loadData();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setUnactivating(false);
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        const text = noteText.trim();
        if (!text || !unit) return;
        setSavingNote(true);
        setError(null);
        try {
            const payload = {
                unitId: unit.unitId,
                note: text,
                noteType,
            };
            if (unit.currentCapId) payload.capId = unit.currentCapId;
            const created = await createUnitNote(payload);
            setNotes((prev) => [created, ...prev]);
            setNoteText("");
            setNoteType("general");
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSavingNote(false);
    };

    const currentCapLabel = unit?.currentCapId
        ? capShortId(currentCap, unit.currentCapId)
        : "None";
    const currentCapSite = capSiteLabel(currentCap);

    const simLabel = sim
        ? [sim.carrier, sim.phoneNumber ? formatPhoneNumber(sim.phoneNumber) || sim.phoneNumber : null, sim.iccid]
              .filter(Boolean)
              .join(" · ")
        : "None assigned";

    return (
        <div className="units-page admin-page">
            <header className="admin-header">
                <nav className="brand-breadcrumb">
                    <Link to="/admin">Admin</Link>
                    <span className="brand-breadcrumb-sep">/</span>
                    <Link to="/admin/units">JobSight units</Link>
                    <span className="brand-breadcrumb-sep">/</span>
                    <span className="brand-breadcrumb-current">
                        {unit ? sticker(unit.unitId) : "Unit"}
                    </span>
                </nav>
                <h1 className="admin-title">
                    {unit ? sticker(unit.unitId) : "JobSight unit"}
                    {unit && (
                        <span style={{ marginLeft: 12, fontSize: 16, verticalAlign: "middle" }}>
                            <Label bsStyle={statusStyle(lifecycle)}>{lifecycle}</Label>
                        </span>
                    )}
                </h1>
            </header>

            {error && <div className="units-error">{error}</div>}
            {unit && pendingReplaceMessage(unit) && (
                <div className="units-pending-replace">
                    {pendingReplaceMessage(unit)}
                    <Button
                        bsSize="xsmall"
                        bsStyle="default"
                        style={{marginLeft: 12}}
                        disabled={replacing}
                        onClick={handleCancelStagedReplace}
                    >
                        Cancel replacement
                    </Button>
                </div>
            )}
            {loading && <div className="units-loading">Loading unit…</div>}
            {!loading && !unit && !error && (
                <div className="units-empty">Unit not found.</div>
            )}

            {unit && (
                <>
                    <div className="units-meta-grid">
                        <div>
                            <span className="units-meta-label">Sticker / unit ID</span>
                            <div className="units-meta-value">
                                <span className="units-sticker">{sticker(unit.unitId)}</span>
                                <CopyToClipboard text={unit.unitId}>
                                    <Button
                                        bsSize="xsmall"
                                        bsStyle="default"
                                        title="Copy full unit ID"
                                        style={{ marginLeft: 8 }}
                                    >
                                        <Glyphicon glyph="duplicate" />
                                    </Button>
                                </CopyToClipboard>
                                <div className="units-id-muted">{unit.unitId}</div>
                            </div>
                        </div>
                        <div>
                            <span className="units-meta-label">Status</span>
                            <FormControl
                                componentClass="select"
                                value={lifecycle}
                                disabled={savingStatus}
                                onChange={(e) => handleStatusChange(e.target.value)}
                            >
                                {STATUS_OPTIONS.map((status) => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </FormControl>
                            {lifecycle === "SHIPPED" && (
                                <Button
                                    bsSize="xsmall"
                                    bsStyle="warning"
                                    style={{ marginTop: 8 }}
                                    disabled={savingStatus}
                                    onClick={handleMarkArrived}
                                    title="Package arrived at the jobsite; still waiting on customer activation"
                                >
                                    Mark arrived
                                </Button>
                            )}
                        </div>
                        <div>
                            <span className="units-meta-label">Port / SSH</span>
                            <UnitPortField
                                unit={unit}
                                cap={currentCap}
                                onError={setError}
                                onSaved={(updated, port) => {
                                    setUnit(updated);
                                    if (unit.currentCapId && port != null) {
                                        setCaps((prev) =>
                                            prev.map((row) =>
                                                row.capId === unit.currentCapId
                                                    ? {...row, port}
                                                    : row
                                            )
                                        );
                                    }
                                }}
                            />
                        </div>
                        <div>
                            <span className="units-meta-label">Architecture</span>
                            <div className="units-meta-value">{unit.architecture || "—"}</div>
                        </div>
                        <div>
                            <span className="units-meta-label">Current cap</span>
                            <div className="units-meta-value">
                                {unit.currentCapId ? (
                                    <Link to={`/caps/${unit.currentCapId}`}>{currentCapLabel}</Link>
                                ) : (
                                    "None"
                                )}
                                {currentCap?.status && (
                                    <span style={{ marginLeft: 8 }}>
                                        <Label bsStyle={capStatusStyle(currentCap.status)}>
                                            {currentCap.status}
                                        </Label>
                                    </span>
                                )}
                                {currentCapSite && (
                                    <div className="units-id-muted">{currentCapSite}</div>
                                )}
                            </div>
                        </div>
                        <div>
                            <span className="units-meta-label">SIM</span>
                            <div className="units-meta-value">{simLabel}</div>
                        </div>
                        <div>
                            <span className="units-meta-label">Added</span>
                            <div className="units-meta-value">{formatDate(unit.addDate)}</div>
                        </div>
                    </div>

                    <div className="units-detail-actions">
                        {currentCap && (
                            <Button
                                bsStyle="primary"
                                disabled={loading}
                                onClick={() => setShowCapModal(true)}
                            >
                                <Glyphicon glyph="pencil" /> Assign builder / edit cap
                            </Button>
                        )}
                        <Button
                            bsStyle="warning"
                            disabled={cycling || replacing || assigningCap || unactivating || loading}
                            onClick={handleCycle}
                        >
                            <Glyphicon glyph="plus" /> New rental (keep sticker)
                        </Button>
                        {unit.currentCapId && (lifecycle === "DEPLOYED" || currentCap?.status === "ACTIVE") && (
                            <Button
                                bsStyle="danger"
                                disabled={cycling || replacing || assigningCap || unactivating || loading}
                                onClick={handleUnactivate}
                            >
                                <Glyphicon glyph="ban-circle" /> {unactivating ? "Unactivating…" : "Unactivate cap"}
                            </Button>
                        )}
                        {unit.currentCapId && (
                            <Button
                                bsStyle="danger"
                                disabled={cycling || replacing || assigningCap || unactivating || loading}
                                onClick={() => handleReplace()}
                            >
                                <Glyphicon glyph="transfer" /> Stage new replacement unit
                            </Button>
                        )}
                        {unit.currentCapId && (
                            <Button
                                bsStyle={updates.status === "UPDATE_AVAILABLE" ? "warning" : "default"}
                                disabled={scheduling || loading || unactivating || updates.status === "UPDATE_AVAILABLE"}
                                onClick={handleScheduleUpdate}
                            >
                                {scheduling ? "Scheduling…" : updates.status === "UPDATE_AVAILABLE" ? "Update scheduled" : "Schedule software update"}
                            </Button>
                        )}
                        <Button bsStyle="default" onClick={loadData} disabled={loading || cycling || replacing || assigningCap || unactivating}>
                            <Glyphicon glyph="refresh" /> Refresh
                        </Button>
                    </div>
                    <div className="units-note-form">
                        <FormControl
                            type="text"
                            placeholder="Existing BUILD cap sticker"
                            value={capStickerInput}
                            onChange={(e) => setCapStickerInput(e.target.value)}
                            style={{flex: "0 1 220px"}}
                        />
                        <Button
                            bsStyle="default"
                            disabled={loading || cycling || replacing || assigningCap || unactivating || capStickerInput.trim().length < 8}
                            onClick={handleAssignExisting}
                        >
                            {assigningCap ? "Assigning…" : "Assign BUILD cap"}
                        </Button>
                    </div>
                    {unit.currentCapId && (
                        <div className="units-note-form">
                            <FormControl
                                type="text"
                                placeholder="Replacement unit sticker"
                                value={replacementStickerInput}
                                onChange={(e) => setReplacementStickerInput(e.target.value)}
                                style={{flex: "0 1 220px"}}
                            />
                            <Button
                                bsStyle="danger"
                                disabled={loading || cycling || replacing || assigningCap || unactivating || replacementStickerInput.trim().length < 8}
                                onClick={handleReplaceWithSticker}
                            >
                                {replacing ? "Staging…" : "Set as replacement"}
                            </Button>
                        </div>
                    )}

                    <section className="units-section">
                        <h2>Software</h2>
                        <div className="units-software-grid">
                            <div>
                                <span className="units-meta-label">Status</span>
                                <div>
                                    {updates.status ? (
                                        <Label bsStyle={softwareStatusStyle(updates.status)}>
                                            {updates.status === "UPDATE_AVAILABLE" ? "AVAILABLE" : updates.status}
                                        </Label>
                                    ) : (
                                        "—"
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="units-meta-label">Health</span>
                                <div>
                                    {currentCap?.health ? (
                                        <Label bsStyle={healthStyle(currentCap.health)}>{currentCap.health}</Label>
                                    ) : (
                                        "—"
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="units-meta-label">Updated</span>
                                <div className="units-meta-value">{formatDateOnly(updates.last_updated)}</div>
                            </div>
                            <div>
                                <span className="units-meta-label">HTTPS keys</span>
                                <div>
                                    {(() => {
                                        const https = httpsKeysDisplay(updates.https_keys);
                                        return https.text === "—" ? "—" : <Label bsStyle={https.bsStyle}>{https.text}</Label>;
                                    })()}
                                </div>
                            </div>
                            <div>
                                <span className="units-meta-label">Hostname</span>
                                <div className="units-meta-value">{updates.hostname || "—"}</div>
                            </div>
                            <div>
                                <span className="units-meta-label">Timezone</span>
                                <div className="units-meta-value">
                                    {formatTimezoneDisplay(updates.timezone) || "—"}
                                </div>
                            </div>
                            <div>
                                <span className="units-meta-label">YOLO</span>
                                <div className="units-meta-value">{updates.yolo != null ? updates.yolo : "—"}</div>
                            </div>
                            <div>
                                <span className="units-meta-label">MP4 / 24/7</span>
                                <div className="units-meta-value">
                                    {yesNo(updates.mediamtx)} / {yesNo(updates.footage)}
                                </div>
                            </div>
                        </div>
                    </section>

                    <UnitSimPanel
                        unitId={unit.unitId}
                        sim={sim}
                        onSimChanged={(next) => setSim(next)}
                    />

                    <section className="units-section">
                        <h2>Caps</h2>
                        {caps.length === 0 ? (
                            <div className="units-empty">No caps assigned to this unit yet.</div>
                        ) : (
                            <div className="units-table-wrapper">
                                <Table striped condensed hover className="units-table">
                                    <thead>
                                        <tr>
                                            <th>Cap</th>
                                            <th>Status</th>
                                            <th>Health</th>
                                            <th>Added</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {caps.map((cap) => {
                                            const isCurrent = cap.capId === unit.currentCapId;
                                            const siteLabel = capSiteLabel(cap);
                                            return (
                                                <tr
                                                    key={cap.capId}
                                                    className={isCurrent ? "units-current-row" : ""}
                                                >
                                                    <td>
                                                        <Link to={`/caps/${cap.capId}`}>
                                                            {capShortId(cap, cap.capId)}
                                                        </Link>
                                                        <div className="units-id-muted">
                                                            {cap.capId}
                                                            {isCurrent ? " · current" : ""}
                                                        </div>
                                                        {siteLabel && (
                                                            <div className="units-id-muted">{siteLabel}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Label bsStyle={capStatusStyle(cap.status)}>
                                                            {cap.status || "—"}
                                                        </Label>
                                                    </td>
                                                    <td>{cap.health || "—"}</td>
                                                    <td>{formatDate(cap.addDate)}</td>
                                                    <td>
                                                        <CopyToClipboard text={cap.capId}>
                                                            <Button bsSize="xsmall" bsStyle="default" title="Copy cap ID">
                                                                <Glyphicon glyph="duplicate" />
                                                            </Button>
                                                        </CopyToClipboard>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </section>

                    <section className="units-section">
                        <h2>Notes</h2>
                        <form className="units-note-form" onSubmit={handleAddNote}>
                            <FormControl
                                componentClass="textarea"
                                placeholder="Hardware note (survives cap cycles)"
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                            />
                            <FormControl
                                componentClass="select"
                                className="units-note-type"
                                value={noteType}
                                onChange={(e) => setNoteType(e.target.value)}
                            >
                                {NOTE_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </FormControl>
                            <Button
                                type="submit"
                                bsStyle="primary"
                                disabled={savingNote || !noteText.trim()}
                            >
                                Add note
                            </Button>
                        </form>
                        {notes.length === 0 ? (
                            <div className="units-empty">No notes yet.</div>
                        ) : (
                            <div className="units-table-wrapper">
                                <Table striped condensed className="units-table">
                                    <thead>
                                        <tr>
                                            <th>When</th>
                                            <th>Type</th>
                                            <th>Cap</th>
                                            <th>Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notes.map((note) => (
                                            <tr key={note.unitNoteId}>
                                                <td>{formatDate(note.addDate)}</td>
                                                <td>
                                                    <Label bsStyle="default">{note.noteType || "general"}</Label>
                                                </td>
                                                <td>
                                                    {note.capId ? (
                                                        <Link to={`/caps/${note.capId}`}>
                                                            {sticker(note.capId)}
                                                        </Link>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                                <td className="units-note-text">{note.note}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </section>

                    <section className="units-section">
                        <h2>Health log</h2>
                        {healthLog.length === 0 ? (
                            <div className="units-empty">No health entries for this unit.</div>
                        ) : (
                            <div className="units-table-wrapper">
                                <Table striped condensed className="units-table">
                                    <thead>
                                        <tr>
                                            <th>When</th>
                                            <th>Cap</th>
                                            <th>Health</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {healthLog.map((entry) => (
                                            <tr key={entry.capHealthId}>
                                                <td>
                                                    {formatDate(entry.updateDate || entry.addDate)}
                                                </td>
                                                <td>
                                                    {entry.capId ? (
                                                        <Link to={`/caps/${entry.capId}`}>
                                                            {sticker(entry.capId)}
                                                        </Link>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                                <td className="units-health-json">
                                                    {formatHealth(entry.health)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </section>
                </>
            )}
            {currentCap && (
                <CapModal
                    cap={currentCap}
                    show={showCapModal}
                    setShowCapModal={setShowCapModal}
                />
            )}
        </div>
    );
}
